import React, { useState } from "react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "../../../components/ui/dialog";
import { api } from "../../../lib/api";
import { toast } from "sonner";

/** v1.14 — accountable adjustments.
 *  - adjustment_type is REQUIRED (count_correction / found_unrecorded / breakage_damage / data_entry_fix / other)
 *  - note is REQUIRED (≥3 chars)
 *  - breakage_damage routes to kind=write_off on the backend → flows into breakage report
 */
const TYPE_OPTIONS = [
  { value: "count_correction", label: "Count correction", help: "Physical count differs from system." },
  { value: "found_unrecorded", label: "Found (previously unrecorded)", help: "Stock turned up that was never logged." },
  { value: "breakage_damage", label: "Breakage / Damage", help: "⚠ Recorded as write-off, appears in breakage report." },
  { value: "data_entry_fix", label: "Data-entry fix", help: "Correcting an earlier entry mistake." },
  { value: "other", label: "Other", help: "Explain in the note." },
];

export default function AdjustStockDialog({ open, onOpenChange, items, sites, onSuccess }) {
  const [itemId, setItemId] = useState("");
  const [site, setSite] = useState("");
  const [delta, setDelta] = useState("");
  const [adjType, setAdjType] = useState("");
  const [note, setNote] = useState("");
  const [busy, setBusy] = useState(false);

  const reset = () => { setItemId(""); setSite(""); setDelta(""); setAdjType(""); setNote(""); setBusy(false); };
  const pickedItem = items.find((i) => i.item_id === itemId);
  const typeMeta = TYPE_OPTIONS.find((t) => t.value === adjType);
  const isBreakage = adjType === "breakage_damage";

  const submit = async () => {
    if (!itemId || !site || delta === "") {
      toast.error("Item, site and delta are required");
      return;
    }
    let d = parseInt(delta, 10);
    if (Number.isNaN(d) || d === 0) {
      toast.error("Delta must be a non-zero number");
      return;
    }
    if (!adjType) {
      toast.error("Adjustment type is required");
      return;
    }
    if (!note.trim() || note.trim().length < 3) {
      toast.error("Note is required (min 3 characters) — explain why");
      return;
    }
    if (isBreakage) {
      d = -Math.abs(d); // breakage is always a reduction
    }
    setBusy(true);
    try {
      await api.post("/inventory/stock/adjust", {
        item_id: itemId, site, delta: d, adjustment_type: adjType, note: note.trim(),
      });
      reset();
      onOpenChange(false);
      onSuccess?.();
    } catch (e) {
      const d2 = e?.response?.data?.detail;
      const msg = typeof d2 === "string" ? d2 : (Array.isArray(d2) ? d2[0]?.msg : null) || "Adjustment failed";
      toast.error(msg);
    } finally {
      setBusy(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={(v) => { if (!v) reset(); onOpenChange(v); }}>
      <DialogContent className="max-w-md rounded-2xl" data-testid="adjust-stock-dialog">
        <DialogHeader>
          <DialogTitle>Adjust stock</DialogTitle>
          <DialogDescription>
            Every adjustment is logged immutably with type, note and actor. Breakage adjustments route to the breakage report — pick &ldquo;Breakage / Damage&rdquo; only when stock was lost/damaged.
          </DialogDescription>
        </DialogHeader>
        <div className="space-y-3 mt-2">
          <Field label="Item" required>
            <select data-testid="adjust-item" value={itemId} onChange={(e) => setItemId(e.target.value)} className={inputCls}>
              <option value="">Select…</option>
              {items.map((i) => <option key={i.item_id} value={i.item_id}>{i.name} ({i.unit})</option>)}
            </select>
            {pickedItem && (
              <div className="mt-1.5 inline-flex items-center gap-1.5 text-[11px] font-semibold text-olive bg-olive/10 border border-olive/30 rounded-full px-2.5 py-1" data-testid="adjust-item-label">
                <span>●</span>
                <span className="truncate">{pickedItem.name}</span>
                <span className="text-slate-400 font-normal">· {pickedItem.unit}</span>
              </div>
            )}
          </Field>
          <div className="grid grid-cols-2 gap-3">
            <Field label="Site" required>
              <select data-testid="adjust-site" value={site} onChange={(e) => setSite(e.target.value)} className={inputCls}>
                <option value="">Select…</option>
                {sites.map((s) => <option key={s} value={s}>{s}</option>)}
              </select>
            </Field>
            <Field label={isBreakage ? "Quantity (will be removed)" : "Delta (signed)"} required>
              <input
                data-testid="adjust-delta"
                type="number"
                value={delta}
                onChange={(e) => setDelta(e.target.value)}
                className={inputCls}
                placeholder={isBreakage ? "e.g. 3" : "+10 or -3"}
              />
            </Field>
          </div>
          <Field label="Adjustment type" required>
            <select data-testid="adjust-type" value={adjType} onChange={(e) => setAdjType(e.target.value)} className={inputCls}>
              <option value="">Select…</option>
              {TYPE_OPTIONS.map((t) => <option key={t.value} value={t.value}>{t.label}</option>)}
            </select>
            {typeMeta && (
              <p className={"text-[10px] mt-1 " + (isBreakage ? "text-red-600 font-medium" : "text-slate-500")} data-testid="adjust-type-help">
                {typeMeta.help}
              </p>
            )}
          </Field>
          <Field label="Note" required>
            <textarea
              data-testid="adjust-note"
              rows={2}
              value={note}
              onChange={(e) => setNote(e.target.value)}
              className={inputCls + " resize-none"}
              placeholder="Explain the adjustment so it can be reviewed later (min 3 chars)"
            />
          </Field>
        </div>
        <DialogFooter className="mt-4">
          <button data-testid="adjust-cancel" onClick={() => onOpenChange(false)} className="rounded-full px-4 py-2 text-xs font-semibold border border-slate-200 bg-white text-slate-700 hover:bg-slate-50">Cancel</button>
          <button
            data-testid="adjust-submit"
            onClick={submit}
            disabled={busy}
            className={"rounded-full px-4 py-2 text-xs font-semibold text-white disabled:opacity-50 " + (isBreakage ? "bg-red-600 hover:bg-red-700" : "bg-olive hover:opacity-90")}
          >
            {busy ? "Saving…" : (isBreakage ? "Record breakage" : "Apply adjustment")}
          </button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

const inputCls = "w-full rounded-xl border border-slate-200 bg-white px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-olive/30";
const Field = ({ label, children, required }) => (
  <label className="block">
    <div className="text-[11px] font-medium uppercase tracking-wider text-slate-500 mb-1">
      {label}
      {required && <span className="text-red-500 ml-0.5">*</span>}
    </div>
    {children}
  </label>
);
